/**
 * FleetManager V3 - Pool Status Widget
 * Le tableau de bord du CONDUCTEUR : sa garde en cours, ses réservations à
 * venir, ses écarts (retard, défaut bloquant) — et rien du parc.
 *
 * Ajouté le 2026-09-13 (constat JGS, compte pool.test@cd31) : le profil
 * `conducteur` affichait des widgets de flotte entière (véhicules, alertes,
 * échéances) au lieu de SON espace. Ce widget consomme `poolAPI.getHome()`
 * (`GET /drivers/me/pool`, déjà scopé conducteur côté serveur — ADR-010 §3.3),
 * jamais un endpoint de flotte : aucune donnée à filtrer côté client.
 *
 * `data.pool` vient de useDashboardData.js, qui bascule sur cette source pour
 * le profil `conducteur` au lieu des fetches vehicleAPI/insuranceAPI/etc.
 */

import React from 'react';
import { Car, Calendar, AlertTriangle, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';
import { useFormat } from '../../../../i18n/format.js';

function ReservationRow({ reservation, onNavigate }) {
  const { t, fmt } = useFormat();
  const plate = reservation.vehicle?.license_plate;
  const flags = reservation.flags || {};
  const hasDeviation = !!(flags.late || flags.blocking_defects || flags.at_risk);

  return (
    <div
      onClick={() => onNavigate?.('pool_driver')}
      className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-900 dark:text-white truncate">
          {plate || t('dashboard.pool.vehicleUnassigned')}
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-500">
          {fmt.relative(reservation.planned_start)} → {fmt.relative(reservation.planned_end)}
        </div>
      </div>
      {hasDeviation && (
        <span className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
          <AlertTriangle size={12} />
          {t('dashboard.pool.deviation')}
        </span>
      )}
      <ChevronRight size={14} className="text-gray-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

export function PoolStatusWidget({ pool, onNavigate }) {
  const { t } = useTranslation();
  const activeCustody = pool?.active_custody || null;
  const upcoming = (pool?.reservations || []).filter(
    (r) => !activeCustody || r.id !== activeCustody.reservation?.id
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Car} iconColor="text-amber-400">{t('dashboard.pool.title')}</CardTitle>
      </CardHeader>

      {/* Compte sans fiche conducteur (un admin qui prévisualise ce profil) :
          on le dit, on ne fait pas semblant d'avoir un pool vide. */}
      {pool?.not_linked && (
        <div className="mb-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm text-gray-600 dark:text-slate-300">
          {t('dashboard.pool.notLinked')}
        </div>
      )}

      {activeCustody ? (
        <div
          onClick={() => onNavigate?.('pool_driver')}
          className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 cursor-pointer"
        >
          <div className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-1">
            {t('dashboard.pool.activeCustody')}
          </div>
          <div className="text-sm text-gray-900 dark:text-white">
            {activeCustody.vehicle?.make} {activeCustody.vehicle?.model} — {activeCustody.vehicle?.license_plate}
          </div>
        </div>
      ) : (
        <div className="text-center py-3">
          <div className="text-sm text-gray-500 dark:text-slate-400">{t('dashboard.pool.noActiveCustody')}</div>
        </div>
      )}

      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400 mb-1">
        <Calendar size={12} />
        {t('dashboard.pool.myReservations')}
      </div>

      {upcoming.length > 0 ? (
        <div className="space-y-1">
          {upcoming.slice(0, 4).map((r) => (
            <ReservationRow key={r.id} reservation={r} onNavigate={onNavigate} />
          ))}
        </div>
      ) : (
        <div className="text-center py-2">
          <div className="text-xs text-gray-500 dark:text-slate-400">{t('dashboard.pool.noReservations')}</div>
        </div>
      )}

      <button
        onClick={() => onNavigate?.('pool_driver')}
        className="w-full mt-2 py-2 text-xs text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors flex items-center justify-center gap-1"
      >
        {t('dashboard.pool.goToPool')}
        <ChevronRight size={12} />
      </button>
    </Card>
  );
}

export default PoolStatusWidget;
